
import { useState } from 'react';

interface ConsultationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ConsultationModal({ isOpen, onClose }: ConsultationModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    phone: '',
    email: '',
    preferredTime: '',
    currentSituation: '',
    mainConcern: '',
    japaneseLevel: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d38ifsc23rr1ef6e00vg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      if (response.ok) {
        setSubmitted(true);
        setTimeout(() => {
          onClose();
          setSubmitted(false);
          setFormData({
            name: '',
            age: '',
            phone: '',
            email: '',
            preferredTime: '',
            currentSituation: '',
            mainConcern: '',
            japaneseLevel: ''
          });
        }, 3000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
            <i className="ri-check-line text-3xl text-green-600"></i>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-4">신청이 완료되었습니다!</h3>
          <p className="text-gray-600 mb-6">
            24시간 내에 연락드려서 상담 일정을 조율하겠습니다.<br />
            조금만 기다려 주세요!
          </p>
          <button
            onClick={onClose}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-3 rounded-full transition-colors cursor-pointer"
          >
            확인
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">무료 카운슬링 신청</h2>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl text-gray-500"></i>
            </button>
          </div>
          <p className="text-gray-600 mt-2">
            30분 무료 상담으로 당신만의 해결책을 찾아보세요
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6" data-readdy-form id="consultation-form">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                이름 *
              </label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="성함을 입력해 주세요"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                나이 *
              </label>
              <input
                type="number"
                name="age"
                required
                min="18"
                max="60"
                value={formData.age}
                onChange={(e) => setFormData({...formData, age: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="연령을 입력해 주세요"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                연락처 *
              </label>
              <input
                type="tel"
                name="phone"
                required
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="010-0000-0000"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                이메일 *
              </label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="이메일을 입력해 주세요"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                상담 희망 시간 *
              </label>
              <select
                name="preferredTime"
                required
                value={formData.preferredTime}
                onChange={(e) => setFormData({...formData, preferredTime: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
              >
                <option value="">시간대를 선택해 주세요</option>
                <option value="평일 오전 (9-12시)">평일 오전 (9-12시)</option>
                <option value="평일 오후 (13-18시)">평일 오후 (13-18시)</option>
                <option value="평일 저녁 (19-22시)">평일 저녁 (19-22시)</option>
                <option value="주말 오전 (9-12시)">주말 오전 (9-12시)</option>
                <option value="주말 오후 (13-18시)">주말 오후 (13-18시)</option>
                <option value="주말 저녁 (19-22시)">주말 저녁 (19-22시)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                일본어 수준 *
              </label>
              <select
                name="japaneseLevel"
                required
                value={formData.japaneseLevel}
                onChange={(e) => setFormData({...formData, japaneseLevel: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
              >
                <option value="">수준을 선택해 주세요</option>
                <option value="전혀 모름">전혀 모름</option>
                <option value="초급 (인사 정도)">초급 (인사 정도)</option>
                <option value="중급 (일상 대화 가능)">중급 (일상 대화 가능)</option>
                <option value="고급 (비즈니스 수준)">고급 (비즈니스 수준)</option>
              </select>
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              현재 상황 *
            </label>
            <select
              name="currentSituation"
              required
              value={formData.currentSituation}
              onChange={(e) => setFormData({...formData, currentSituation: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
            >
              <option value="">상황을 선택해 주세요</option>
              <option value="일본인 여성과 아직 만나본 적 없음">일본인 여성과 아직 만나본 적 없음</option>
              <option value="온라인에서 대화 중이지만 어려움 겪음">온라인에서 대화 중이지만 어려움 겪음</option>
              <option value="몇 번 만났지만 진전이 없음">몇 번 만났지만 진전이 없음</option>
              <option value="현재 사귀고 있지만 소통에 문제">현재 사귀고 있지만 소통에 문제</option>
              <option value="기타">기타</option>
            </select>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              가장 큰 고민 (자유롭게 작성해 주세요) *
            </label>
            <textarea
              name="mainConcern"
              required
              maxLength={500}
              value={formData.mainConcern}
              onChange={(e) => setFormData({...formData, mainConcern: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm h-32 resize-none"
              placeholder="현재 가장 어려워하는 부분이나 고민을 자세히 적어주세요. 상담 시 더욱 구체적인 도움을 받으실 수 있습니다. (최대 500자)"
            />
            <div className="text-right text-xs text-gray-500 mt-1">
              {formData.mainConcern.length}/500자
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full text-white text-lg font-bold py-4 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${
                isSubmitting 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700 transform hover:scale-105'
              }`}
            >
              {isSubmitting ? '신청 중...' : '무료 카운슬링 신청하기'}
            </button>
            
            <p className="text-center text-sm text-gray-500 mt-4">
              신청 후 24시간 내에 상담 일정 조율을 위해 연락드립니다
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
